import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import FormData from "form-data";
import {
  getStoreDetail,
  getCategoriesList,
  getStoreBrands,
  getRecentlyBrowseProduct,
} from "../redux/actions/action-creator";

import Preloader from "../helper/Preloader";
import Header from "../components/Header";
import Brand from "../components/Brand";
import Newsletter from "../components/Newsletter";
import NewArrivalOne from "../components/NewArrivalOne";
import Footer from "../components/Footer";
import BottomFooter from "../components/BottomFooter";
import ScrollToTop from "react-scroll-to-top";
import ColorInit from "../helper/ColorInit";

import StoreCategory from "../components/stores/StoreCategory";
import ShoppingBreadcrumb from "../components/ShoppingBreadcrumb";
import MarketPromoBanner from "../components/MarketPromoBanner";
import MobileCatalogSearch from "../components/MobileCatalogSearch";
import {
  DEFAULT_DELIVERY_LOCATION,
  LOCATION_STORAGE_KEY,
} from "../utils/locationDefaults";
import { setPageSeo } from "../utils/seo";
import "./HomePage.css";

const DEFAULT_LOCATION = DEFAULT_DELIVERY_LOCATION;
const EMPTY_ROUTE_STATE = {};
const EMPTY_STORE = {};

const getValue = (item, keys) => {
  const matchedKey = keys.find(
    (key) => item?.[key] !== null && item?.[key] !== undefined && item?.[key] !== ""
  );

  return matchedKey ? item[matchedKey] : "";
};

const buildFormData = (payload) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      formData.append(key, value);
    }
  });

  return formData;
};

const getSavedUserLocation = () => {
  try {
    const savedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
    return savedLocation ? JSON.parse(savedLocation) : null;
  } catch (error) {
    return null;
  }
};

const normalizeStoreDetail = (data, fallbackStore) => {
  if (Array.isArray(data?.cell_contents)) return data.cell_contents[0] || fallbackStore;
  if (Array.isArray(data?.response_data)) return data.response_data[0] || fallbackStore;
  if (Array.isArray(data?.data)) return data.data[0] || fallbackStore;
  return data || fallbackStore;
};

const getIdFromCategorySlug = (slug) => {
  const matchedId = String(slug || "").match(/(\d+)$/);
  return matchedId ? matchedId[1] : "";
};

const StoreCategoryPage = () => {
  
    const dispatch = useDispatch();
    const { storeId, categorySlug } = useParams();
    const routeLocation = useLocation();
    const routeState = routeLocation.state || EMPTY_ROUTE_STATE;
    const selectedStore = routeState.store || EMPTY_STORE;
    const {
      latitude,
      longitude,
      user_location,
      country_id,
      area,
    } = useSelector((state) => state.auth);
    const [storeLoading, setStoreLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [brandsLoading, setBrandsLoading] = useState(true);
    const [recentlybrowseLoading, setRecentlybrowseLoading] = useState(true);
    const [storeDetail, setStoreDetail] = useState(null);
    const [categories, setCategories] = useState(null);
    const [brands, setBrands] = useState(null);
    const [recentlybrowse, setRecentlybrowse] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const routeStoreId = storeId || getIdFromCategorySlug(categorySlug);
    const businessSegmentId =
      routeState.businessSegmentId ||
      getValue(selectedStore, [
        "business_segment_id",
        "businessSegmentId",
        "store_id",
        "storeId",
        "id",
        "branch_id",
      ]) ||
      routeStoreId ||
      "";
    const selectedStoreId =
      routeState.storeId ||
      getValue(selectedStore, ["store_id", "storeId", "id", "branch_id"]) ||
      routeStoreId ||
      "";
    const selectedSegmentId =
      routeState.segmentId ||
      getValue(selectedStore, ["segment_id", "segmentId", "segment"]) ||
      "";

    useEffect(() => setSearchTerm(""), [businessSegmentId]);

    const getActiveUserLocation = useCallback(() => {
      const savedLocation = getSavedUserLocation();
      const reduxLocation = user_location || {};

      return {
        latitude:
          latitude ??
          reduxLocation?.latitude ??
          savedLocation?.latitude ??
          DEFAULT_LOCATION.latitude,
        longitude:
          longitude ??
          reduxLocation?.longitude ??
          savedLocation?.longitude ??
          DEFAULT_LOCATION.longitude,
        country_id:
          country_id ??
          reduxLocation?.country_id ??
          reduxLocation?.countryId ??
          savedLocation?.country_id ??
          savedLocation?.countryId ??
          DEFAULT_LOCATION.country_id,
        merchant_id:
          getValue(selectedStore, ["merchant_id", "merchantId"]) ||
          (reduxLocation?.merchant_id ??
          reduxLocation?.merchantId ??
          savedLocation?.merchant_id ??
          savedLocation?.merchantId ??
          DEFAULT_LOCATION.merchant_id),
        area:
          area ??
          reduxLocation?.area ??
          reduxLocation?.area_id ??
          savedLocation?.area ??
          savedLocation?.area_id,
      };
    }, [area, country_id, latitude, longitude, selectedStore, user_location]);

    const handleonload = useCallback(async () => {
      setStoreLoading(true);
      setCategoriesLoading(true);
      setBrandsLoading(true);
      setRecentlybrowseLoading(true);

      const userLocation = getActiveUserLocation();

      if (!businessSegmentId) {
        setStoreDetail(Object.keys(selectedStore).length ? selectedStore : null);
        setStoreLoading(false);
        setCategoriesLoading(false);
        setBrandsLoading(false);
        setRecentlybrowse(null);
        setRecentlybrowseLoading(false);
        return;
      }

      /*STORE DETAIL FORM DATA*/
      const storePayload = {
        business_segment_id: businessSegmentId,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      };

      if (selectedStoreId && String(selectedStoreId) !== String(businessSegmentId)) {
        storePayload.store_id = selectedStoreId;
      }

      let storeData = buildFormData(storePayload);
    
      /*CATEGORY FORM DATA*/
      const categoryPayload = {
        business_segment_id: businessSegmentId,
        segment_id: selectedSegmentId,
        calling_from: "website_screen",
        is_website_calling: 1,
      };
      let categoryData = buildFormData(categoryPayload);

      /*BRAND DATA*/
      const brandPayload = {
        business_segment_id: businessSegmentId,
        segment_id: selectedSegmentId,
        merchant_id: userLocation.merchant_id,
        calling_from: "category_screen",
        page: 1,
        per_page: 10,
      };
      let brandData = buildFormData(brandPayload);

      /*RECENTLY BROWSE DATA*/
      const recentProductPayload = {
        segment_id: selectedSegmentId,
        business_segment_id: businessSegmentId,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      };
      let recentProductData = buildFormData(recentProductPayload);

      console.log("StoreCategoryPage API Payloads", {
        getStoreDetail: storePayload,
        getCategoriesList: categoryPayload,
        getStoreBrands: brandPayload,
        getRecentlyBrowseProduct: recentProductPayload,
      });

      const storeRequest = dispatch(getStoreDetail(storeData))
        .then((response) => {
          if (response?.result) {
            setStoreDetail(normalizeStoreDetail(response.data, selectedStore));
          }
        })
        .catch((error) => {
          console.log("Store detail API Error:", error?.message);
          if (Object.keys(selectedStore).length) setStoreDetail(selectedStore);
        })
        .finally(() => setStoreLoading(false));

      const categoryRequest = dispatch(getCategoriesList(categoryData))
        .then((response) => {
          if (response?.result) setCategories(response.data);
        })
        .catch((error) => console.log("Store category API Error:", error?.message))
        .finally(() => setCategoriesLoading(false));

      const brandsRequest = dispatch(getStoreBrands(brandData))
        .then((response) => {
          if (response?.result) setBrands(response.data?.response_data?.[0] || null);
        })
        .catch((error) => console.log("Brands API Error:", error?.message))
        .finally(() => setBrandsLoading(false));

      const recentProductRequest = dispatch(getRecentlyBrowseProduct(recentProductData))
        .then((response) => {
          setRecentlybrowse(response?.result ? response.data || null : null);
        })
        .catch((error) => {
          console.log("Recently browse API Error:", error?.message);
          setRecentlybrowse(null);
        })
        .finally(() => setRecentlybrowseLoading(false));

      await Promise.all([storeRequest, categoryRequest, brandsRequest, recentProductRequest]);
  }, [
    businessSegmentId,
    dispatch,
    getActiveUserLocation,
    routeState,
    selectedSegmentId,
    selectedStore,
    selectedStoreId,
  ]);

  useEffect(() => {
     handleonload();
  }, [handleonload]);

  const pageTitle = storeDetail?.title || selectedStore?.title || "Stores";

  useEffect(() => {
    setPageSeo({
      title: `${pageTitle} | RGHT NOW`,
      description: `Shop categories, brands, and products from ${pageTitle} on RGHT NOW.`,
      path: routeLocation.pathname,
    });
  }, [pageTitle, routeLocation.pathname]);
  
  return (

    <div className="bg-white home-page-shell store-category-page-shell">
    
      {/* Preloader */}
      <Preloader />

      {/* ScrollToTop */}
      <ScrollToTop smooth color="#299E60" />

      {/* ColorInit */}
      <ColorInit color={false} />

      {/* Header */}
      <Header category={true} />

      {/* Breadcrumb */}
      <ShoppingBreadcrumb page="categories" store={storeDetail || selectedStore} businessSegmentId={businessSegmentId} segmentId={selectedSegmentId} storeId={selectedStoreId} />

      <MarketPromoBanner page="store_category" businessSegmentId={businessSegmentId}
        segmentId={selectedSegmentId || getValue(storeDetail, ["segment_id", "segmentId", "segment"])} enabled={!storeLoading} />
      <MobileCatalogSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search for categories" />

      {/* SuperMarkets */}
      <StoreCategory
        storeDetail={storeDetail}
        categories={categories}
        storeLoading={storeLoading}
        categoriesLoading={categoriesLoading}
        businessSegmentId={businessSegmentId}
        segmentId={selectedSegmentId || getValue(storeDetail, ["segment_id", "segmentId", "segment"])}
        segment={routeState.segment}
        storeId={selectedStoreId}
        searchTerm={searchTerm}
      />

      {/* NewArrivalOne */}
      <NewArrivalOne
        recentlybrowse={recentlybrowse}
        loading={recentlybrowseLoading}
        productDetailContext={{ ...routeState, store: storeDetail || selectedStore, storeSlug: categorySlug }}
        cartContext={{
          segment_id: selectedSegmentId,
          business_segment_id: businessSegmentId,
        }}
      />

      {/* Brand */}
      <Brand brands={brands} loading={brandsLoading}/>
      
      {/* Newsletter */}
      <Newsletter />

      {/* Footer */}
      <Footer />
      
      {/* BottomFooter */}
      <BottomFooter />

    </div>
  );
};

export default StoreCategoryPage;
